import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, FileText, Timer } from 'lucide-react';
import type { AttemptState, InviteExamSet, InviteLanding } from 'shared';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { translateApiError } from '@/lib/api-error';
import { writeSessionToken } from '@/lib/exam-session';

/**
 * หน้าส่วนตัวของผู้ที่ถูกนำเข้ารายชื่อล่วงหน้า
 * ลิงก์ผูกกับหลักสูตร จึงลิสต์ชุดข้อสอบที่เปิดอยู่ให้เลือก แทนที่จะเดาว่าควรเปิดชุดไหน
 *
 * ชื่อ/อีเมลมาจากรายชื่อที่นำเข้าไว้ แก้ที่นี่ไม่ได้ (server ไม่รับจาก client อยู่แล้ว)
 * แต่ยังต้องติ๊กยินยอม PDPA เองทุกครั้ง — ติ๊กแทนกันไม่ได้
 */
export function InviteLandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token = '' } = useParams();

  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot — ผู้ใช้จริงมองไม่เห็น

  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => apiFetch<InviteLanding>(`/public/invite/${encodeURIComponent(token)}`),
    retry: false,
  });

  const start = useMutation({
    mutationFn: (examSetId: string) =>
      apiFetch<AttemptState>(`/public/invite/${encodeURIComponent(token)}/start`, {
        method: 'POST',
        body: { examSetId, consent, ...(website ? { website } : {}) },
      }),
    onSuccess: (state) => {
      if (state.sessionToken) writeSessionToken(state.sessionToken);
      queryClient.setQueryData(['attempt'], state);
      void navigate(`/i/${token}/exam`);
    },
  });

  const onStart = (examSetId: string): void => {
    if (!consent) {
      setConsentError(true);
      return;
    }
    setConsentError(false);
    start.mutate(examSetId);
  };

  const label = (examSet: InviteExamSet): string =>
    examSet.status === 'in_progress' ? t('invite.resume') : t('invite.start');

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-xl flex-col gap-5 px-5 py-8">
      <header className="flex items-start justify-between gap-4">
        <p className="text-sm font-medium text-slate-500">{t('app.org')}</p>
        <LanguageSwitcher />
      </header>

      {invite.isPending && <p className="text-slate-600">{t('common.loading')}</p>}

      {invite.isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <p className="flex items-start gap-2 font-medium text-red-800">
            <AlertTriangle aria-hidden className="mt-0.5 h-5 w-5 shrink-0" />
            {translateApiError(invite.error, t)}
          </p>
        </div>
      )}

      {invite.isSuccess && (
        <>
          <div>
            <p className="text-sm font-medium text-slate-500">{invite.data.courseTitle}</p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              {t('invite.greeting', { name: invite.data.fullName })}
            </h1>
            <p className="mt-2 leading-relaxed text-slate-600">{t('invite.intro')}</p>
          </div>

          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-base font-semibold text-slate-900">{t('invite.yourInfo')}</h2>
            <dl className="mt-3 grid gap-2 text-sm">
              <div className="flex gap-2">
                <dt className="w-28 shrink-0 text-slate-500">{t('register.fullName')}</dt>
                <dd className="text-slate-900">{invite.data.fullName}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-28 shrink-0 text-slate-500">{t('register.email')}</dt>
                <dd className="break-all text-slate-900">{invite.data.email}</dd>
              </div>
              {invite.data.organization && (
                <div className="flex gap-2">
                  <dt className="w-28 shrink-0 text-slate-500">{t('register.organization')}</dt>
                  <dd className="text-slate-900">{invite.data.organization}</dd>
                </div>
              )}
            </dl>
            <p className="mt-3 text-sm leading-relaxed text-slate-500">{t('invite.emailNote')}</p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-base font-semibold text-slate-900">{t('invite.examSets')}</h2>

            {invite.data.examSets.length === 0 && (
              <p className="rounded-xl border border-slate-200 bg-white p-5 text-slate-600">
                {t('invite.noExamSets')}
              </p>
            )}

            {invite.data.examSets.map((examSet) => (
              <article
                key={examSet.examSetId}
                className="rounded-xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg font-semibold text-slate-900">{examSet.title}</h3>
                  {examSet.status === 'submitted' && (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-sm font-medium text-emerald-800">
                      <CheckCircle2 aria-hidden className="h-4 w-4" />
                      {t('invite.status.submitted')}
                    </span>
                  )}
                  {examSet.status === 'in_progress' && (
                    <span className="shrink-0 rounded-full bg-amber-50 px-2.5 py-1 text-sm font-medium text-amber-900">
                      {t('invite.status.in_progress')}
                    </span>
                  )}
                  {examSet.status === 'expired' && (
                    <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-sm font-medium text-slate-700">
                      {t('invite.status.expired')}
                    </span>
                  )}
                </div>

                <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                  <div className="flex items-center gap-1.5">
                    <FileText aria-hidden className="h-4 w-4" />
                    <dt className="sr-only">{t('landing.questionCount')}</dt>
                    <dd>{t('landing.questionCountValue', { count: examSet.questionCount })}</dd>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Timer aria-hidden className="h-4 w-4" />
                    <dt className="sr-only">{t('landing.duration')}</dt>
                    <dd>
                      {examSet.durationMinutes
                        ? t('landing.durationValue', { count: examSet.durationMinutes })
                        : t('landing.noTimeLimit')}
                    </dd>
                  </div>
                </dl>

                {examSet.instructions && (
                  <p className="mt-3 whitespace-pre-wrap leading-relaxed text-slate-700">
                    {examSet.instructions}
                  </p>
                )}

                <div className="mt-4">
                  {examSet.canStart ? (
                    <Button
                      size="lg"
                      className="w-full"
                      disabled={start.isPending}
                      onClick={() => {
                        onStart(examSet.examSetId);
                      }}
                    >
                      {start.isPending ? t('invite.starting') : label(examSet)}
                    </Button>
                  ) : (
                    <p className="text-sm font-medium text-slate-500">
                      {examSet.isOpen ? t('invite.done') : t('invite.closed')}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </section>

          {/* honeypot กันบอท — ซ่อนจากผู้ใช้จริงและจาก screen reader */}
          <div className="hidden" aria-hidden>
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(event) => {
                setWebsite(event.target.value);
              }}
            />
          </div>

          <section>
            <h2 className="sr-only">{t('invite.consentTitle')}</h2>
            <label className="flex items-start gap-3 rounded-lg border border-slate-300 bg-white p-3.5">
              <input
                type="checkbox"
                checked={consent}
                onChange={(event) => {
                  setConsent(event.target.checked);
                  if (event.target.checked) setConsentError(false);
                }}
                aria-describedby={consentError ? 'consent-error' : undefined}
                className="mt-1 h-5 w-5 shrink-0 accent-slate-900"
              />
              <span className="text-sm leading-relaxed text-slate-700">
                {t('register.consent')}{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" className="underline">
                  {t('register.consentLink')}
                </a>
              </span>
            </label>
            {consentError && (
              <p id="consent-error" className="mt-1 text-sm text-red-700">
                {t('register.errors.consent')}
              </p>
            )}
          </section>

          {start.isError && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
              {translateApiError(start.error, t)}
            </p>
          )}
        </>
      )}
    </main>
  );
}
